import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invalidate } from '@react-three/fiber';
import { AreaFillSymbolType, isAreaFillMovementLocked } from '../../../../types/floorplannerTypes';
import { floorplannerStore } from '../../../../store/floorplannerStore';
import { BONI_CHECKBOX_CLASSNAME } from '../../../../constants/brandColors';
import PanelSection from '../components/PanelSection';
import UnitHeader from '../components/UnitHeader';
import { panelObjectTitleRowClassName } from '../propertyPanelUtils';

type AreaFillSectionProps = {
  selectedAreaFillSymbols: AreaFillSymbolType[];
  displayedAreaFillSymbols: AreaFillSymbolType[];
};

const AreaFillSection: React.FC<AreaFillSectionProps> = ({
  selectedAreaFillSymbols,
  displayedAreaFillSymbols,
}) => {
  const { t } = useTranslation();
  /** Per-block open state so multi-select area fills can collapse independently. */
  const [openById, setOpenById] = useState<Record<string, boolean>>({});

  return (
    <>
      {displayedAreaFillSymbols.map((areaFill) => {
        const liveAreaFill =
          (floorplannerStore.symbolsMap.get(areaFill.id) as AreaFillSymbolType | undefined) ??
          areaFill;
        const movementLocked = isAreaFillMovementLocked(liveAreaFill);
        const open = openById[areaFill.id] ?? true;
        const title =
          selectedAreaFillSymbols.length > 1
            ? t('propertyPanel.multipleRoomAreaFills', {
                count: selectedAreaFillSymbols.length,
              })
            : (liveAreaFill.name ?? t('propertyPanel.roomAreaFill'));

        return (
          <div key={`areaFill-${areaFill.id}`}>
            <div className={panelObjectTitleRowClassName}>
              <UnitHeader title={title} />
            </div>
            <PanelSection
              title={t('propertyPanel.lockMovement')}
              collapsible
              open={open}
              onToggle={() =>
                setOpenById((prev) => ({
                  ...prev,
                  [areaFill.id]: !open,
                }))
              }
              maxHeightClassName="max-h-none"
            >
              <div className="px-3 pb-3 pt-0">
                <div className="flex items-center justify-between rounded border border-[#dedbd2] bg-white p-3">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {t('propertyPanel.lockMovement')}
                    </div>
                    <div className="mt-1 text-xs leading-4 text-gray-500">
                      {t('propertyPanel.unlockToDragAreaFill')}
                    </div>
                  </div>
                  <input
                    id={`lockAreaFill-${liveAreaFill.id}`}
                    className={BONI_CHECKBOX_CLASSNAME}
                    type="checkbox"
                    checked={movementLocked}
                    onChange={(e) => {
                      floorplannerStore.pushToUndoStack();
                      floorplannerStore.updateSymbolProperty(
                        liveAreaFill.id,
                        'locked',
                        e.target.checked,
                      );
                      floorplannerStore.setDirty();
                      invalidate();
                    }}
                  />
                </div>
              </div>
            </PanelSection>
          </div>
        );
      })}
    </>
  );
};

export default AreaFillSection;
